// src/pages/EmailsPage.js
import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, getDocs, orderBy, doc, updateDoc, getDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import './EmailsPage.css';

const EmailsPage = () => {
  const { currentUser } = useAuth();
  const [emails, setEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [emailBody, setEmailBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [loadingBody, setLoadingBody] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  
  // 25.09.25 3:15pm - Add search state variables
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchStats, setSearchStats] = useState({ total: 0, showing: 0 });
  
  // 24.11.25 15:30 - Add files state for attachment content search
  const [filesData, setFilesData] = useState([]);

  useEffect(() => {
    const fetchEmails = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      
      try {
        const db = getFirestore();
        // 05.09.25 18:20 AEST - Remove jane.doe fallback
const userEmail = currentUser.email;
        
        const emailsQuery = query(
          collection(db, 'Users', userEmail, 'Emails'),
          orderBy('timestamp', 'desc')
        );
        
        const emailsSnapshot = await getDocs(emailsQuery);
        const emailsList = emailsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            from: data.sender || data.from || "Unknown"
          };
        });
        
        console.log('09.09.25 14:20 AEST - Total emails loaded:', emailsList.length);
        emailsList.forEach(email => {
          console.log('Email:', email.subject, 'Attachments:', email.attachments);
        });
        setEmails(emailsList);
        // 25.09.25 3:20pm - Initialize filtered emails
        setFilteredEmails(emailsList);
        setSearchStats({ total: emailsList.length, showing: emailsList.length });
        
        // 24.11.25 15:35 - Fetch Files data for attachment content search
        try {
          const filesQuery = query(
            collection(db, 'Users', userEmail, 'Files'),
            orderBy('uploadedAt', 'desc')
          );
          const filesSnapshot = await getDocs(filesQuery);
          const filesList = filesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFilesData(filesList);
          console.log('24.11.25 15:35 - Files loaded for search:', filesList.length);
        } catch (filesError) {
          console.log('24.11.25 15:35 - Files fetch error (non-critical):', filesError);
        }
        
// 20.02.26 - Removed auto-select of first email
        // Users now choose which email to open, consistent with Files page behaviour
      } catch (err) {
        console.error('Error fetching emails:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmails();
  }, [currentUser]);
  
// 25.09.25 3:25pm - Add search filtering when emails or search term changes
useEffect(() => {
  if (!searchTerm) {
    setFilteredEmails(emails);
    setSearchStats({ total: emails.length, showing: emails.length });
    return;
  }
  
  const searchLower = searchTerm.toLowerCase();
  const searchWords = searchLower.split(' ').filter(word => word.length > 0);
  
  const filtered = emails.filter(email => {
    // Search email subject
    if (email.subject?.toLowerCase().includes(searchLower)) return true;
    
    // Search sender/from
    if (email.from?.toLowerCase().includes(searchLower)) return true;
    if (email.sender?.toLowerCase().includes(searchLower)) return true;
    
    // Search category and subcategory
    if (email.category?.toLowerCase().includes(searchLower)) return true;
    if (email.subCategory?.toLowerCase().includes(searchLower)) return true;
    
    // Search email body content
    if (email.bodyPlain?.toLowerCase().includes(searchLower)) return true;
    
    // Search labels
    if (email.labels?.some(label => 
      label.toLowerCase().includes(searchLower))) return true;
    
    // Search importance level
    if (email.importance?.toLowerCase().includes(searchLower)) return true;
    
// 24.11.25 16:15 - Search attachment content (extractedText from Files)
    // Match files using Gmail messageId (not Firestore doc ID)
    const emailAttachmentFiles = filesData.filter(file => {
      const isEmailAttachment = file.emailId === email.messageId ||
                                file.emailMessageId === email.messageId ||
                                file.emailId === email.threadId ||
                                file.path?.includes(email.messageId);
      return isEmailAttachment;
    });
    
    // Search extractedText in matched attachment files
    if (emailAttachmentFiles.some(file => 
      file.extractedText?.toLowerCase().includes(searchLower))) {
      return true;
    }
    
    // Search attachment keywords
    if (emailAttachmentFiles.some(file => 
      file.extractedKeywords?.some(keyword => 
        keyword.toLowerCase().includes(searchLower)))) {
      return true;
    }
    
    // Multi-word search (including attachment content)
    if (searchWords.length > 1) {
      const attachmentTexts = emailAttachmentFiles.map(f => f.extractedText || '').join(' ');
      
      const allEmailText = [
        email.subject,
        email.from,
        email.sender,
        email.category,
        email.subCategory,
        email.bodyPlain,
        attachmentTexts,
        ...(email.labels || [])
      ].join(' ').toLowerCase();
      
      return searchWords.every(word => allEmailText.includes(word));
    }
    
    return false;
  });
  
  setFilteredEmails(filtered);
  setSearchStats({ total: emails.length, showing: filtered.length });
}, [emails, searchTerm, filesData]);

  const fetchEmailBody = async (email) => {
    if (!email || email.bodyFetched) {
      setEmailBody(email?.body || '');
      return;
    }
    
    setLoadingBody(true);
    
    try {
      const storage = getStorage();
      const db = getFirestore();
      const userEmail = currentUser.email;
      
      // First check if body exists in the email object
      if (email.body) {
        setEmailBody(email.body);
        return;
      }
      
      // Next try to get body from a separate EmailContent collection in Firestore
      try {
        const emailContentDoc = await getDoc(doc(db, 'Users', userEmail, 'EmailContents', email.id));
        if (emailContentDoc.exists()) {
          const contentData = emailContentDoc.data();
          if (contentData.body) {
            setEmailBody(contentData.body);
            return;
          }
        }
      } catch (error) {
        console.log('Email content not found in EmailContents collection:', error);
      }
      
      // Finally try to get body from Storage
      try {
        // Path could be directly in email object or follow a convention
        const bodyPath = email.bodyPath || `Users/${userEmail}/email_bodies/${email.id}.html`;
        const bodyRef = ref(storage, bodyPath);
        const bodyUrl = await getDownloadURL(bodyRef);
        
        // Fetch the content
        const response = await fetch(bodyUrl);
        const bodyContent = await response.text();
        setEmailBody(bodyContent);
        
        // Optionally update the email object with the body to avoid fetching again
        updateDoc(doc(db, 'Users', userEmail, 'Emails', email.id), {
          bodyFetched: true,
          // Don't store body in Firestore if it's large, to avoid document size limits
          body: bodyContent.length < 1000 ? bodyContent : undefined
        }).catch(err => console.error('Error updating email with body:', err));
      } catch (error) {
        console.error('Email body not found in Storage:', error);
        setEmailBody('<p>Could not load email content</p>');
      }
    } catch (error) {
      console.error('Error fetching email body:', error);
      setEmailBody('<p>Error loading email content</p>');
    } finally {
      setLoadingBody(false);
    }
  };
  
  const fetchAttachments = async (email) => {
    if (!email || (!email.attachments && !email.attachmentFiles && !email.hasAttachments)) {
      setAttachments([]);
      return;
    }
    
    setLoadingAttachments(true);
    
    try {
      const storage = getStorage();
      const userEmail = currentUser.email;
      
     // 09.09.25 14:35 AEST - Handle different attachment field names
     const attachmentData = email.attachmentFiles || email.attachments || [];
     let attachmentsList = [];
     
     for (const attachment of attachmentData) {
// Handle different attachment formats
if (typeof attachment === 'string') {
  const targetFilename = attachment;
  console.log('09.09.25 15:15 AEST - Looking for file ending with:', targetFilename);
  
  try {
    // Import Firebase Storage list function
    const { listAll } = await import('firebase/storage');
    
    // List all files in email attachments folder
    const folderRef = ref(storage, `Users/${userEmail}/email attachments folder/`);
    const listResult = await listAll(folderRef);
    
    // Find file that ends with our target filename
    const matchingItem = listResult.items.find(item => {
      const fullName = item.name;
      console.log('Checking file:', fullName, 'ends with', targetFilename, '?', fullName.endsWith(targetFilename));
      return fullName.endsWith(targetFilename);
    });
    
    if (matchingItem) {
      console.log('Found matching file:', matchingItem.name);
      const downloadUrl = await getDownloadURL(matchingItem);
      
      attachmentsList.push({
        name: targetFilename,
        path: matchingItem.fullPath,
        downloadUrl: downloadUrl,
        size: 0
      });
    } else {
      console.log('No file found ending with:', targetFilename);
      attachmentsList.push({
        name: targetFilename,
        error: true
      });
    }
  } catch (error) {
  console.error(`Error finding attachment ${targetFilename}:`, error);
  attachmentsList.push({
    name: targetFilename,
    error: true
  });
}
           
        } else if (typeof attachment === 'object') {
          // It already has metadata
          try {
            // If path exists, get download URL
            if (attachment.path) {
              const attachmentRef = ref(storage, attachment.path);
              const downloadUrl = await getDownloadURL(attachmentRef);
              
              attachmentsList.push({
                ...attachment,
                downloadUrl: downloadUrl
              });
            } else {
              attachmentsList.push(attachment);
            }
          } catch (error) {
            console.error(`Error getting download URL for ${attachment.name}:`, error);
            attachmentsList.push({
              ...attachment,
              error: true
            });
          }
        }
      }
      
      setAttachments(attachmentsList);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };
  
  const handleSelectEmail = async (email) => {
    setSelectedEmail(email);
    setShowAttachments(false); // Reset attachments view when changing email
    
    // Fetch attachments and body for the selected email
    fetchAttachments(email);
    fetchEmailBody(email);
    
    // Mark as read if not already read
    if (email && !email.read && currentUser) {
      try {
        const db = getFirestore();
        await updateDoc(doc(db, 'Users', currentUser.email, 'Emails', email.id), {
          read: true
        });
        
        // Update local state
        setEmails(prevEmails => 
          prevEmails.map(e => 
            e.id === email.id ? { ...e, read: true } : e
          )
        );
      } catch (error) {
        console.error('Error marking email as read:', error);
      }
    }
  };
  
  const toggleAttachments = () => {
    setShowAttachments(!showAttachments);
  };
  
  // Format date for display - more compact format
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // If today, show only time
      if (date >= today) {
        return date.toLocaleTimeString('en-AU', { 
          hour: '2-digit', 
          minute: '2-digit'
        });
      }
      
      // If yesterday, show "Yesterday"
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date >= yesterday && date < today) {
        return 'Yesterday';
      }
      
      // If this year, show day and month
      if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString('en-AU', { 
          day: 'numeric',
          month: 'short'
        });
      }
      
      // Otherwise show date with year
      return date.toLocaleDateString('en-AU', { 
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (err) {
      return '';
    }
  };
  
  // Get icon for attachment based on file extension
  const getAttachmentIcon = (filename) => {
    if (!filename) return '📄';
    const extension = filename.split('.').pop().toLowerCase();
    
    switch (extension) {
      case 'pdf': return '📕';
      case 'doc':
      case 'docx': return '📘';
      case 'xls':
      case 'xlsx': return '📗';
      case 'ppt':
      case 'pptx': return '📙';
      case 'jpg':
      case 'jpeg':
      case 'png': return '🖼️';
      default: return '📄';
    }
  };
  
 // 25.09.25 3:30pm - Use filtered emails for display
 const emailsToDisplay = searchTerm ? filteredEmails : emails;
  
 return (
   <div className="emails-page">
<div className="emails-header">
       <h1>My Emails</h1>
       
       <div className="header-controls">
         <button 
           className="search-toggle-btn"
           onClick={() => setShowSearch(!showSearch)}
           title="Toggle email search"
         >
           🔍 {showSearch ? 'Hide' : 'Search'}
         </button>
       </div>
     </div>
     
     {/* 25.09.25 3:35pm - Add search container */}
     {showSearch && (
       <div className="search-container">
         <div className="search-input-wrapper">
           <input
             type="text"
             placeholder="Search emails, subjects, senders, content..."
             value={searchTerm}
             onChange={(e) => setSearchTerm(e.target.value)}
             className="search-input"
           />
           {searchTerm && (
             <button 
               onClick={() => setSearchTerm('')}
               className="clear-search"
             >
               ✕
             </button>
           )}
         </div>
         {searchTerm && (
           <div className="search-results-info">
             Showing {searchStats.showing} of {searchStats.total} emails
             {searchStats.showing !== searchStats.total && (
               <span className="search-match"> matching "{searchTerm}"</span>
             )}
           </div>
         )}
       </div>
     )}
      
{/* 20.02.26 - Add email-selected class to hide list when viewing email on mobile */}
<div className={`email-container ${selectedEmail ? 'email-selected' : ''}`}>
        <div className="email-list">
          {loading ? (
            <div className="loading-indicator">Loading emails...</div>
          ) : emailsToDisplay.length === 0 ? (
            <div className="empty-state">
              {searchTerm ? 'No emails found matching your search' : 'No emails found'}
            </div>
          ) : (
            <ul>
              {emailsToDisplay.map(email => (
                <li 
                  key={email.id} 
                  className={`email-item ${selectedEmail?.id === email.id ? 'selected' : ''} ${!email.read ? 'unread' : ''}`}
                  onClick={() => {
                    console.log('09.09.25 14:20 AEST - Email clicked:', email);
                    console.log('Has attachments:', !!(email.attachments || email.attachmentFiles || email.hasAttachments));
                    console.log('attachmentFiles:', email.attachmentFiles);
                    console.log('hasAttachments:', email.hasAttachments);
                    handleSelectEmail(email);
                  }}
                >
                  <div className="email-sender">{email.from}</div>
                  <div className="email-info">
                    <span className="email-subject">{email.subject || 'No Subject'}</span>
                    <span className="email-time">{formatDate(email.timestamp)}</span>
                  </div>
                  {(email.attachments || email.attachmentFiles) && (email.attachments || email.attachmentFiles).length > 0 && (
                    <div className="email-has-attachment">
                      <span className="paperclip-icon">📎</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        
        <div className="email-preview">
          {selectedEmail ? (
            <>
              {/* 20.02.26 - Back button returns to list on mobile */}
              <button 
                className="email-back-button"
                onClick={() => setSelectedEmail(null)}
              >
                ← Back to emails
              </button>

              <div className="email-header">
                <h2>{selectedEmail.subject || 'No Subject'}</h2>
                <div className="email-metadata">
                  <div className="email-from"><strong>From:</strong> {selectedEmail.from}</div>
                  <div className="email-to"><strong>To:</strong> {selectedEmail.to || selectedEmail.recipient || currentUser?.email}</div>
                  <div className="email-date"><strong>Date:</strong> {formatDate(selectedEmail.timestamp)}</div>
                  
                  {/* Show paperclip if email has attachments */}
                  {(selectedEmail.attachments || selectedEmail.attachmentFiles) && (selectedEmail.attachments || selectedEmail.attachmentFiles).length > 0 && (
                    <div className="email-attachment-indicator">
                      <button 
                        className="attachment-toggle-btn" 
                        onClick={toggleAttachments}
                        title={`${(selectedEmail.attachments || selectedEmail.attachmentFiles || []).length} attachment(s)`}
                      >
                        <span className="paperclip-icon">📎</span>
                        <span className="attachment-count">{(selectedEmail.attachments || selectedEmail.attachmentFiles).length}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Only show attachments if toggle is clicked */}
              {(showAttachments && (selectedEmail.attachments || selectedEmail.attachmentFiles) && (selectedEmail.attachments || selectedEmail.attachmentFiles).length > 0) && (
                <div className="email-attachments">
                  <div className="attachment-note">
                    <span className="info-icon">ⓘ</span>
                    <p>Attachments are automatically saved and categorized using AI and NDIS-related categories.</p>
                  </div>
                  
                  {loadingAttachments ? (
                    <div className="loading-indicator">Loading attachments...</div>
                  ) : attachments.length > 0 ? (
                    <div className="attachments-list">
                      {attachments.map((attachment, index) => (
                        <div key={index} className="attachment-item">
                          <span className="attachment-icon">
                            {getAttachmentIcon(attachment.name)}
                          </span>
                          <span className="attachment-name">{attachment.name}</span>
                          {attachment.path ? (
  <button 
    className="attachment-view-btn"
    onClick={async (e) => {
      e.stopPropagation();
      console.log('09.09.25 15:25 AEST - Email attachment clicked:', attachment);
      
      try {
        // Generate fresh authenticated download URL (same as files)
        const { getStorage, ref, getDownloadURL } = await import('firebase/storage');
        
        const storage = getStorage();
        const fileRef = ref(storage, attachment.path);
        
        const authenticatedURL = await getDownloadURL(fileRef);
        console.log('Opening email attachment with fresh authenticated URL');
        
        window.open(authenticatedURL, '_blank');
        
      } catch (error) {
        console.error('Error accessing email attachment:', error);
        alert('Unable to open attachment - access denied');
      }
    }}
  >
    View
  </button>
) : (
  <span className="attachment-error">Cannot view</span>
)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">No attachments found</div>
                  )}
                </div>
              )}
              
              <div className="email-actions">
  <button 
    onClick={() => {
      // 09.09.25 15:00 AEST - Open email in browser tab (no print dialog)
      const emailWindow = window.open('', '_blank');
      emailWindow.document.write(`
        <html>
        <head>
          <title>${selectedEmail.subject}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .email-header { border-bottom: 1px solid #ccc; padding-bottom: 10px; margin-bottom: 20px; }
            .header-line { margin: 5px 0; }
          </style>
        </head>
        <body>
          <div class="email-header">
            <div class="header-line"><strong>From:</strong> ${selectedEmail.from || selectedEmail.sender || 'Unknown'}</div>
            <div class="header-line"><strong>To:</strong> ${selectedEmail.to || currentUser?.email}</div>
            <div class="header-line"><strong>Subject:</strong> ${selectedEmail.subject || 'No Subject'}</div>
            <div class="header-line"><strong>Date:</strong> ${formatDate(selectedEmail.timestamp)}</div>
          </div>
          ${emailBody}
        </body>
        </html>
      `);
      emailWindow.document.close();
    }}
    className="view-email-button"
  >
    View Email
  </button>
  
  <button 
    onClick={() => {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
        <head><title>Print: ${selectedEmail.subject}</title></head>
        <body>
          <div><strong>From:</strong> ${selectedEmail.from || selectedEmail.sender || 'Unknown'}</div>
          <div><strong>To:</strong> ${selectedEmail.to || currentUser?.email}</div>
          <div><strong>Subject:</strong> ${selectedEmail.subject || 'No Subject'}</div>
          <div><strong>Date:</strong> ${formatDate(selectedEmail.timestamp)}</div>
          <hr>
          ${emailBody}
        </body>
        </html>
      `);
      printWindow.print();
    }}
    className="print-button"
  >
    Print Email
  </button>
</div>


              <div className="email-body">
                {loadingBody ? (
                  <div className="loading-indicator">Loading email content...</div>
                ) : emailBody ? (
                  <div dangerouslySetInnerHTML={{ __html: emailBody }} />
                ) : (
                  <p>Could not load email content</p>
                )}
              </div>
            </>
          ) : (
            <div className="no-email-selected">
              <p>Select an email to view</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailsPage;